import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { id } from "date-fns/locale";
import { jwtDecode } from "jwt-decode";
import { useIsMobile } from "@/hooks/use-mobile";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatToMask(format: string): string {
  return format.replace(/[a-zA-Z]/g, "_");
}

export const formatFileName = (name: string) => {
  if (!name) return "Tidak ada nama file";
  const tempName = name?.split("/");
  const newName = tempName[tempName.length - 1];
  return newName;
};

export function formatErrorMessages(
  errorData: Record<string, string[] | string>
): string {
  return Object.entries(errorData)
    .map(([field, messages]) => {
      const readableField = field
        .replace(/([A-Z])/g, " $1") // camelCase to space
        .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter

      const text =
        Array.isArray(messages) && messages.length > 0
          ? messages.map((msg) => `• ${readableField}: ${msg}`).join("\n")
          : `• ${readableField}: ${messages}`;

      return text;
    })
    .join("\n");
}

export function formatDateDay(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  try {
    return format(new Date(dateString), "dd MMMM yyyy", { locale: id });
  } catch {
    return "-";
  }
}

export function createOptions(data: any) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((item: any) => {
    return {
      value: item.id.toString(),
      label: item.name,
    };
  });
}

// jwt
type JwtPayload = {
  id: number;
  name: string;
  role: string;
  imageUrl: string | null;
  iat: number;
  exp: number;
};

export function getUserIdFromToken(): number | null {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(/accessToken=([^;]+)/);
  if (!match) return null;

  try {
    const token = match[1];
    const decoded: JwtPayload = jwtDecode(token);
    return decoded.id;
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
}

export function formatGender(data: string | null | undefined) {
  if (!data) return "-";
  switch (data.toLowerCase()) {
    case "male":
      return "Laki-laki";
    case "female":
      return "Perempuan";
    default:
      return "Tidak Diketahui";
  }
}

export function countNotificationTimer(
  createDate: string,
  isMobile: boolean = false
) {
  if (!createDate) return "";
  const createdDate = new Date(createDate);
  if (isNaN(createdDate.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - createdDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return `baru saja`;
  if (diffMinutes < 60)
    return `${diffMinutes} ${isMobile ? "menit" : "menit yang lalu"}`;
  if (diffHours < 24) return `${diffHours} jam ${isMobile ? "" : "yang lalu"}`;
  if (diffDays === 1) return "Kemarin";
  return `${diffDays} ${isMobile ? "hari" : "hari yang lalu"}`;
}

export function getStatusText(status: string): string {
  const statusMap: Record<string, string> = {
    Pending: "Menunggu Verifikasi",
    VerifiedByAdmin: "Verifikasi Admin",
    RejectedByAdmin: "Dipertimbangkan",
    VerifiedByMembers: "Terverifikasi",
    RejectedByMembers: "Dipertimbangkan",
  };

  return statusMap[status] || status;
}

export function getStatusTextColor(status: string): string {
  const colorMap: Record<string, string> = {
    Pending: "text-gray-700",
    VerifiedByAdmin: "text-blue-600",
    RejectedByAdmin: "text-yellow-600",
    VerifiedByMembers: "text-green-600",
    RejectedByMembers: "text-yellow-600",
  };

  return colorMap[status] || "text-gray-700";
}

export function getStatusBgColor(status: string): string {
  const colorsMap: Record<string, string> = {
    Pending: "bg-gray-700",
    VerifiedByAdmin: "bg-blue-600",
    RejectedByAdmin: "bg-yellow-600",
    VerifiedByMembers: "bg-green-600",
    RejectedByMembers: "bg-yellow-600",
  };

  return colorsMap[status] || "bg-gray-700";
}

export function getLevelUrgencyText(level: string): string {
  const levelMap: Record<string, string> = {
    Low: "Rendah",
    Medium: "Sedang",
    High: "Tinggi",
  };

  return levelMap[level] || level;
}

/** Helper: base64URL → Uint8Array (untuk VAPID public key) */
export function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Safe);
  return Uint8Array.from([...raw].map((ch) => ch.charCodeAt(0)));
}

export function arrayBufferToBase64(buf: ArrayBuffer | null) {
  if (!buf) return "";
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
